import * as React from 'react';

import classnames from 'classnames';
import {FieldArray} from 'redux-form';

import {Checkbox} from '../checkbox/checkbox';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlAttachmentIcon} from '../icon/svg/html-attachment-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlBoldIcon} from '../icon/svg/html-bold-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlImageIcon} from '../icon/svg/html-image-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlItalicIcon} from '../icon/svg/html-italic-icon';
import {HtmlLinkIcon} from '../icon/svg/html-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlListIcon} from '../icon/svg/html-list-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlUnderlineIcon} from '../icon/svg/html-underline-icon';

import {AttachmentCount} from './attachment-count';
import {FileAttachmentForm} from './file-attachment-form';

import styles from './email-composer-toolbar.module.css';

type Props = {
    toolbarId: string;
    // This is messy. We have multiple ways of attaching files (filestack's old
    // file picker, and the new react component in a redux form). With the old
    // picker we automatically wrap the files in an object, where as with the
    // new picker we use the array of files in form state. Because of this
    // difference we need different handlers.

    // Old way
    onClickAttachFile?: () => any;
    fileAttachments?: any;
    // Redux form way
    fileAttachmentFieldName?: string;
    useSignature?: boolean;
    onChangeUseSignature?: () => void;
    onToolbarLinkButtonClicked: () => void;
    showPlaceholderSelect: boolean;
    disabled?: boolean;
    allowAddingCustomPlaceholders: boolean;
    insertLinkProps?: {
        component: React.ComponentType<{
            onInsertLink: (link: string) => void;
        }>;
        onInsertLink: (link: string) => void;
    };

    // In some cases (like shared email inbox, we want to make space for a send button to the right of the signature
    // as opposed to it being on a separate line below it)
    hasExtraSpaceForSendButton?: boolean;
};

export class EmailComposerToolbar extends React.PureComponent<Props> {
    render() {
        const LinkInsertionComponent = this.props.insertLinkProps
            ? this.props.insertLinkProps.component
            : undefined;

        return (
            <div
                id={this.props.toolbarId}
                // We cannot use a dynamic styleName because quill seems to do some kind of
                // checks before the classname is generated, and it breaks the smart field select.
                // We need to disable pointers because quill turns the select into a span, which can't
                // be disabled like a real select can
                style={this.props.disabled ? {pointerEvents: 'none'} : undefined}
                className={styles['email-composer-toolbar']}
            >
                {/* These are magic classNames that ReactQuill
                will pick up and turn into interactive buttons
                with icons - don't change them! */}
                <button className='ql-bold' disabled={this.props.disabled}>
                    <HtmlBoldIcon size={14} />
                </button>
                <button className='ql-italic' disabled={this.props.disabled}>
                    <HtmlItalicIcon size={14} />
                </button>
                <button className='ql-underline' disabled={this.props.disabled}>
                    <HtmlUnderlineIcon size={14} />
                </button>
                <button className='ql-list' value='bullet' disabled={this.props.disabled}>
                    <HtmlListIcon size={14} />
                </button>
                <button
                    onClick={this.props.onToolbarLinkButtonClicked}
                    className='ql-link'
                    disabled={this.props.disabled}
                >
                    <HtmlLinkIcon size={14} />
                </button>
                <button className='ql-image' disabled={this.props.disabled}>
                    <HtmlImageIcon size={14} />
                </button>
                {this.renderFileAttachmentButton()}
                {this.props.fileAttachments ? (
                    <AttachmentCount attachments={this.props.fileAttachments} />
                ) : null}
                {this.props.showPlaceholderSelect ? (
                    <select
                        disabled={this.props.disabled}
                        className={classnames(
                            'ql-placeholder',
                            this.props.allowAddingCustomPlaceholders
                                ? styles['ql-placeholder']
                                : styles['ql-placeholder--no-custom']
                        )}
                    >
                        <option value='firstName'>First name</option>
                        <option value='lastName'>Last name</option>
                        <option value='companyName'>Company name</option>
                        {this.props.allowAddingCustomPlaceholders ? (
                            <option value='placeholder'>Placeholder</option>
                        ) : undefined}
                    </select>
                ) : null}
                {this.props.onChangeUseSignature ? (
                    <div
                        className={classnames(styles['use-signature'], {
                            [styles['use-signature-with-space']]:
                                this.props.hasExtraSpaceForSendButton,
                        })}
                    >
                        <Checkbox
                            // @ts-expect-error
                            name='useSignature'
                            label='Use your signature'
                            onChange={this.props.onChangeUseSignature}
                            checked={this.props.useSignature}
                            disabled={this.props.disabled}
                        />
                    </div>
                ) : undefined}
                {LinkInsertionComponent &&
                this.props.insertLinkProps &&
                this.props.insertLinkProps.onInsertLink ? (
                    <LinkInsertionComponent
                        onInsertLink={this.props.insertLinkProps.onInsertLink}
                    />
                ) : null}
            </div>
        );
    }

    renderFileAttachmentButton = () => {
        if (this.props.fileAttachmentFieldName) {
            return (
                <FieldArray
                    name={this.props.fileAttachmentFieldName}
                    component={FileAttachmentForm}
                    disabled={this.props.disabled}
                />
            );
        }

        if (this.props.onClickAttachFile) {
            // Render the old file picker button
            return (
                <button
                    onClick={this.props.onClickAttachFile}
                    type='button'
                    disabled={this.props.disabled}
                >
                    <HtmlAttachmentIcon size={16} />
                </button>
            );
        }

        return undefined;
    };
}
